import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { placeAPI, placeReviewAPI } from "@/lib/api";
import { MapPin, ArrowLeft, ExternalLink, Loader2, Youtube, Navigation, Instagram, Bus, Train, Car, Ticket, Sun, ChevronDown, ChevronUp, CheckCircle, AlertTriangle, HelpCircle, ChevronLeft, ChevronRight, Star, Send, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ShareButtons from "@/components/ShareButtons";
import PageSEO from "@/components/PageSEO";
import PhotoGallery from "@/components/PhotoGallery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const categoryIcons = { Restaurants: "🍽️", Temples: "🛕", Beaches: "🏖️", Waterfalls: "💦", Forts: "🏰", Parks: "🌳" };

const getInstagramReelId = (url) => {
  if (!url) return null;
  const match = url.match(/instagram\.com\/(?:reel|p)\/([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
};

/* ─── Image Carousel ─── */
const ImageCarousel = ({ images, name, onOpenGallery }) => {
  const [current, setCurrent] = useState(0);
  if (!images || images.length === 0) return null;

  return (
    <div className="relative rounded-3xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.12)]" data-testid="image-carousel">
      <img src={images[current]} alt={`${name} - ${current + 1}`} className="w-full h-[360px] md:h-[480px] object-cover transition-opacity duration-300 cursor-pointer" onClick={() => onOpenGallery?.(current)} data-testid="carousel-main-image" />
      {images.length > 1 && (
        <>
          <button onClick={() => setCurrent(c => c === 0 ? images.length - 1 : c - 1)} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/90 backdrop-blur-sm hover:bg-white text-coconut-green rounded-full flex items-center justify-center transition-all shadow-lg" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => setCurrent(c => c === images.length - 1 ? 0 : c + 1)} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/90 backdrop-blur-sm hover:bg-white text-coconut-green rounded-full flex items-center justify-center transition-all shadow-lg" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, idx) => (
              <button key={idx} onClick={() => setCurrent(idx)} className={`w-2.5 h-2.5 rounded-full transition-all ${idx === current ? "bg-white w-6" : "bg-white/50"}`} />
            ))}
          </div>
          <span className="absolute top-4 right-4 bg-black/40 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full">{current + 1} / {images.length}</span>
        </>
      )}
      {/* Click to expand hint */}
      <button onClick={() => onOpenGallery?.(current)} className="absolute bottom-4 right-4 bg-black/40 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full hover:bg-black/60 transition-colors flex items-center gap-1.5" data-testid="open-gallery-btn">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
        View Gallery
      </button>
    </div>
  );
};

/* ─── FAQ Accordion ─── */
const FAQItem = ({ faq, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-beach-sand/60 rounded-2xl overflow-hidden bg-white" data-testid={`faq-${idx}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left hover:bg-soft-beige/50 transition-colors">
        <span className="font-medium text-gray-900 text-sm pr-4">{faq.question}</span>
        {open ? <ChevronUp className="w-4 h-4 text-coconut-green flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed border-t border-beach-sand/40 pt-4">{faq.answer}</div>}
    </div>
  );
};

/* ─── Star Rating ─── */
const StarRating = ({ rating, onRate, size = "w-5 h-5", interactive = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button key={star} type="button" disabled={!interactive}
          onClick={() => interactive && onRate?.(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={`${interactive ? "cursor-pointer" : "cursor-default"} transition-colors`}>
          <Star className={`${size} ${star <= (hover || rating) ? "fill-sunset-gold text-sunset-gold" : "text-gray-300"}`} />
        </button>
      ))}
    </div>
  );
};

/* ─── Review Form ─── */
const ReviewForm = ({ placeId, onSubmitted }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (rating === 0) { toast.error("Please select a rating"); return; }
    setSubmitting(true);
    try {
      await placeReviewAPI.submit(placeId, { name: name.trim(), email: email.trim() || null, rating, comment: comment.trim() || null });
      toast.success("Review submitted! It will appear after admin approval.");
      setSubmitted(true);
      onSubmitted?.();
    } catch { toast.error("Failed to submit review"); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="bg-coconut-green/5 rounded-2xl p-6 text-center" data-testid="review-submitted">
        <CheckCircle className="w-10 h-10 text-coconut-green mx-auto mb-3" />
        <p className="font-semibold text-coconut-green">Thank you for your review!</p>
        <p className="text-sm text-gray-500 mt-1">It will be visible after admin approval.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">Your Rating *</label>
        <StarRating rating={rating} onRate={setRating} size="w-7 h-7" interactive={true} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Name *</label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="rounded-xl border-beach-sand/60" data-testid="review-name" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Email (optional)</label>
          <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" type="email" className="rounded-xl border-beach-sand/60" data-testid="review-email" />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1 block">Comment</label>
        <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Share your experience..." rows={3}
          className="w-full rounded-xl border border-beach-sand/60 bg-background px-3 py-2 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coconut-green/30"
          data-testid="review-comment" />
      </div>
      <Button type="submit" disabled={submitting} className="rounded-xl bg-coconut-green hover:bg-coconut-green/90" data-testid="review-submit">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        Submit Review
      </Button>
    </form>
  );
};

/* ─── Review List ─── */
const ReviewList = ({ reviews, averageRating, total }) => {
  if (!reviews || reviews.length === 0) return null;
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="space-y-4" data-testid="reviews-list">
      {/* Summary */}
      <div className="flex items-center gap-4 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-3xl font-bold text-coconut-green">{averageRating}</span>
          <div>
            <StarRating rating={Math.round(averageRating)} size="w-4 h-4" />
            <p className="text-xs text-gray-500 mt-0.5">{total} review{total !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>
      {/* Individual reviews */}
      {reviews.map((review) => (
        <div key={review.id} className="border border-beach-sand/40 rounded-2xl p-5 bg-white" data-testid={`review-${review.id}`}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
              <p className="text-xs text-gray-400">{formatDate(review.created_at)}</p>
            </div>
            <StarRating rating={review.rating} size="w-4 h-4" />
          </div>
          {review.comment && <p className="text-sm text-gray-600 leading-relaxed mt-2">{review.comment}</p>}
        </div>
      ))}
    </div>
  );
};

const PlaceDetailPage = () => {
  const { slug } = useParams();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [similarPlaces, setSimilarPlaces] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    const fetchPlace = async () => {
      try {
        setLoading(true);
        const response = await placeAPI.getBySlug(slug);
        setPlace(response.data);
        // Fetch similar places
        try {
          const similarRes = await placeAPI.getSimilar(response.data.id, 6);
          setSimilarPlaces(similarRes.data);
        } catch {}
        // Fetch reviews
        fetchReviews(response.data.id);
      } catch { setError("Place not found"); }
      finally { setLoading(false); }
    };
    if (slug) fetchPlace();
  }, [slug]);

  const fetchReviews = async (placeId) => {
    try {
      const res = await placeReviewAPI.getApproved(placeId);
      setReviews(res.data.reviews || []);
      setAvgRating(res.data.average_rating || 0);
      setTotalReviews(res.data.total || 0);
    } catch {}
  };

  const getImageUrl = (img) => {
    if (!img) return null;
    if (img.startsWith('/')) return `${BACKEND_URL}${img}`;
    return img;
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const youtubeUrls = place ? (place.youtube_urls?.length > 0 ? place.youtube_urls : (place.youtube_url ? [place.youtube_url] : [])) : [];
  const instagramUrls = place ? (place.instagram_reel_urls?.length > 0 ? place.instagram_reel_urls : (place.instagram_reel_url ? [place.instagram_reel_url] : [])) : [];
  const allImages = place ? [place.image, ...(place.images || [])].filter(Boolean).map(getImageUrl) : [];

  const metaTitle = place ? (place.meta_title || place.name) : 'Loading...';
  const metaDescription = place ? (place.meta_description || place.description?.replace(/<[^>]*>/g, '').slice(0, 160) || '') : '';

  const openGallery = (idx) => { setGalleryIndex(idx); setGalleryOpen(true); };

  if (loading) return (<div className="min-h-screen bg-soft-beige"><Navbar /><div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-coconut-green" /></div><Footer /></div>);
  if (error || !place) return (<div className="min-h-screen bg-soft-beige"><Navbar /><div className="flex flex-col items-center justify-center py-40"><h1 className="font-serif text-2xl text-coconut-green mb-4">Place Not Found</h1><Link to="/places" className="text-coconut-green hover:underline">← Back to Explore Places</Link></div><Footer /></div>);

  const hasTravelInfo = place.nearest_bus_stand || place.nearest_railway_station || place.distance_from_mumbai || place.distance_from_pune || place.entry_fee || place.best_season;

  return (
    <div className="min-h-screen bg-soft-beige" data-testid="place-detail-page">
      <Navbar />
      {place && <PageSEO title={metaTitle} description={metaDescription} path={`/place/${place.slug}`} image={allImages[0]} canonicalUrl={place.canonical_url} />}
      <PhotoGallery images={allImages} initialIndex={galleryIndex} open={galleryOpen} onClose={() => setGalleryOpen(false)} />

      {/* Breadcrumb */}
      <section className="pt-24 pb-4 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Link to="/places" className="inline-flex items-center gap-2 text-gray-500 hover:text-coconut-green transition-colors group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Places</span>
          </Link>
        </div>
      </section>

      {/* Header */}
      <section className="pb-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-coconut-green/8 text-coconut-green rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="text-base">{categoryIcons[place.category] || "📍"}</span>{place.category}
          </span>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-coconut-green mb-3">{place.name}</h1>
          {(place.address || place.location_name) && (
            <p className="flex items-center gap-2 text-gray-500 text-base">
              <MapPin className="w-4 h-4 text-terracotta" />
              {place.address || `${place.sub_location_name ? place.sub_location_name + ', ' : ''}${place.location_name}`}
            </p>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Left Column */}
            <div className="lg:col-span-8 space-y-10">
              {allImages.length > 0 && <ImageCarousel images={allImages} name={place.name} onOpenGallery={openGallery} />}

              {/* Description */}
              {place.description && (
                <div className="bg-white rounded-3xl p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)]" data-testid="description-section">
                  <h2 className="font-serif text-2xl text-coconut-green mb-5">About {place.name}</h2>
                  <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: place.description }} />
                </div>
              )}

              {/* Things To Do */}
              {place.things_to_do?.length > 0 && (
                <div className="bg-white rounded-3xl p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)]" data-testid="things-to-do">
                  <h2 className="font-serif text-2xl text-coconut-green mb-5 flex items-center gap-3">
                    <div className="w-10 h-10 bg-coconut-green/10 rounded-xl flex items-center justify-center"><CheckCircle className="w-5 h-5 text-coconut-green" /></div>
                    Things To Do
                  </h2>
                  <div className="space-y-3">
                    {place.things_to_do.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 rounded-2xl bg-coconut-green/4 hover:bg-coconut-green/8 transition-colors">
                        <span className="w-7 h-7 bg-coconut-green text-white rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold">{idx + 1}</span>
                        <span className="text-gray-700 pt-0.5">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Things To Avoid */}
              {place.things_to_avoid?.length > 0 && (
                <div className="bg-white rounded-3xl p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)]" data-testid="things-to-avoid">
                  <h2 className="font-serif text-2xl text-coconut-green mb-5 flex items-center gap-3">
                    <div className="w-10 h-10 bg-terracotta/10 rounded-xl flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-terracotta" /></div>
                    Things To Avoid
                  </h2>
                  <div className="space-y-3">
                    {place.things_to_avoid.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 rounded-2xl bg-terracotta/4 hover:bg-terracotta/8 transition-colors">
                        <span className="w-7 h-7 bg-terracotta text-white rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold">!</span>
                        <span className="text-gray-700 pt-0.5">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQs */}
              {place.faqs?.length > 0 && (
                <div className="bg-white rounded-3xl p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)]" data-testid="faqs-section">
                  <h2 className="font-serif text-2xl text-coconut-green mb-5 flex items-center gap-3">
                    <div className="w-10 h-10 bg-konkan-teal/10 rounded-xl flex items-center justify-center"><HelpCircle className="w-5 h-5 text-konkan-teal" /></div>
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-3">
                    {place.faqs.map((faq, idx) => <FAQItem key={idx} faq={faq} idx={idx} />)}
                  </div>
                </div>
              )}

              {/* YouTube */}
              {youtubeUrls.length > 0 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-2xl text-coconut-green flex items-center gap-3">
                    <Youtube className="w-6 h-6 text-red-600" /> Videos
                  </h2>
                  <div className={`grid gap-6 ${youtubeUrls.length === 1 ? '' : 'md:grid-cols-2'}`}>
                    {youtubeUrls.map((url, i) => { const e = getYouTubeEmbedUrl(url); if (!e) return null; return <div key={i} className="rounded-2xl overflow-hidden shadow-lg"><div className="aspect-video"><iframe src={e} title={`Video ${i+1}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div></div>; })}
                  </div>
                </div>
              )}

              {/* Instagram */}
              {instagramUrls.length > 0 && (
                <div className="space-y-5">
                  <h2 className="font-serif text-2xl text-coconut-green flex items-center gap-3">
                    <Instagram className="w-6 h-6 text-pink-600" /> Instagram Reels
                  </h2>
                  <div className={`grid gap-6 ${instagramUrls.length === 1 ? 'justify-center' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
                    {instagramUrls.map((url, i) => { const r = getInstagramReelId(url); if (!r) return null; return <div key={i} className="rounded-2xl overflow-hidden shadow-lg bg-white"><div className="flex justify-center p-4"><iframe src={`https://www.instagram.com/reel/${r}/embed`} width="320" height="420" frameBorder="0" scrolling="no" allowFullScreen title={`Reel ${i+1}`} className="rounded-lg" /></div><div className="px-4 pb-4 text-center"><a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-pink-600 hover:text-pink-700 font-medium inline-flex items-center gap-1"><Instagram className="w-3.5 h-3.5" />View on Instagram</a></div></div>; })}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
                <h3 className="font-serif text-lg text-coconut-green mb-4">Share this place</h3>
                <ShareButtons url={`${window.location.origin}/place/${place.slug}`} title={place.name} description={place.description?.replace(/<[^>]*>/g, '').slice(0, 100) || `Explore ${place.name}`} variant="inline" />
              </div>

              {/* Reviews Section */}
              <div className="bg-white rounded-3xl p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)]" data-testid="reviews-section">
                <h2 className="font-serif text-2xl text-coconut-green mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-sunset-gold/10 rounded-xl flex items-center justify-center"><MessageSquare className="w-5 h-5 text-sunset-gold" /></div>
                  Reviews {totalReviews > 0 && <span className="text-base font-normal text-gray-400">({totalReviews})</span>}
                </h2>

                {/* Existing Reviews */}
                {reviews.length > 0 && (
                  <div className="mb-8">
                    <ReviewList reviews={reviews} averageRating={avgRating} total={totalReviews} />
                  </div>
                )}

                {/* Write Review */}
                <div className="border-t border-beach-sand/40 pt-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Write a Review</h3>
                  <ReviewForm placeId={place.id} onSubmitted={() => fetchReviews(place.id)} />
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              <div className="lg:sticky lg:top-24 space-y-6">

                {/* Travel Info */}
                {hasTravelInfo && (
                  <div className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.05)]" data-testid="travel-info-card">
                    <h3 className="font-serif text-lg text-coconut-green mb-5">Travel Information</h3>
                    <div className="space-y-4">
                      {place.best_season && <TravelRow icon={<Sun className="w-5 h-5 text-sunset-gold" />} label="Best Season" value={place.best_season} />}
                      {place.entry_fee && <TravelRow icon={<Ticket className="w-5 h-5 text-konkan-teal" />} label="Entry Fee" value={place.entry_fee} />}
                      {place.nearest_bus_stand && <TravelRow icon={<Bus className="w-5 h-5 text-terracotta" />} label="Nearest Bus Stand" value={place.nearest_bus_stand} />}
                      {place.nearest_railway_station && <TravelRow icon={<Train className="w-5 h-5 text-tropical-green" />} label="Nearest Railway" value={place.nearest_railway_station} />}
                      {place.distance_from_mumbai && <TravelRow icon={<Car className="w-5 h-5 text-gray-500" />} label="From Mumbai" value={place.distance_from_mumbai} />}
                      {place.distance_from_pune && <TravelRow icon={<Car className="w-5 h-5 text-gray-500" />} label="From Pune" value={place.distance_from_pune} />}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
                  <h3 className="font-serif text-lg text-coconut-green mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    {place.map_link && (
                      <a href={place.map_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full p-3.5 bg-coconut-green/8 text-coconut-green rounded-2xl hover:bg-coconut-green/15 transition-colors">
                        <Navigation className="w-5 h-5" /><span className="font-medium text-sm">Get Directions</span><ExternalLink className="w-4 h-4 ml-auto opacity-50" />
                      </a>
                    )}
                    {youtubeUrls.length > 0 && (
                      <a href={youtubeUrls[0]} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full p-3.5 bg-red-50 text-red-600 rounded-2xl hover:bg-red-100 transition-colors">
                        <Youtube className="w-5 h-5" /><span className="font-medium text-sm">Watch Video</span><ExternalLink className="w-4 h-4 ml-auto opacity-50" />
                      </a>
                    )}
                    {instagramUrls.length > 0 && (
                      <a href={instagramUrls[0]} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full p-3.5 bg-gradient-to-r from-purple-50 to-pink-50 text-pink-600 rounded-2xl hover:from-purple-100 hover:to-pink-100 transition-colors">
                        <Instagram className="w-5 h-5" /><span className="font-medium text-sm">View Reel</span><ExternalLink className="w-4 h-4 ml-auto opacity-50" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Google Preferred Source */}
                <a
                  href="https://www.google.com/preferences/source?q=https://kokanhomestays.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full p-3.5 bg-white rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-md transition-all group"
                  data-testid="google-prefer-btn"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">Prefer Kokan Homestays on Google</span>
                </a>

                {/* Map */}
                {place.latitude && place.longitude && (
                  <div className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
                    <h3 className="font-serif text-lg text-coconut-green mb-4">Location</h3>
                    <div className="rounded-2xl overflow-hidden">
                      <iframe title="Map" src={`https://www.openstreetmap.org/export/embed.html?bbox=${place.longitude-0.01}%2C${place.latitude-0.01}%2C${place.longitude+0.01}%2C${place.latitude+0.01}&layer=mapnik&marker=${place.latitude}%2C${place.longitude}`} width="100%" height="200" style={{ border: 0 }} loading="lazy" />
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <a href={`https://www.google.com/maps/search/?api=1&query=${place.latitude},${place.longitude}`} target="_blank" rel="noopener noreferrer" className="text-xs text-coconut-green hover:underline flex items-center gap-1">Google Maps<ExternalLink className="w-3 h-3" /></a>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${place.latitude},${place.longitude}`} target="_blank" rel="noopener noreferrer" className="text-xs text-coconut-green hover:underline flex items-center gap-1">Directions<Navigation className="w-3 h-3" /></a>
                    </div>
                  </div>
                )}

                {/* Area */}
                {(place.location_name || place.sub_location_name) && (
                  <div className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
                    <h3 className="font-serif text-lg text-coconut-green mb-4">Area</h3>
                    {place.location_name && (<Link to={`/location/${place.location_name.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-center gap-2 text-coconut-green hover:underline text-sm"><MapPin className="w-4 h-4" />{place.location_name}</Link>)}
                    {place.sub_location_name && <p className="text-gray-500 text-sm pl-6 mt-1">{place.sub_location_name}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Similar Places */}
      {similarPlaces.length > 0 && (
        <section className="py-16 bg-white" data-testid="similar-places">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-terracotta mb-2">You might also like</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-coconut-green mb-8">Similar Places to Explore</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {similarPlaces.slice(0, 6).map(sp => (
                <Link key={sp.id} to={`/place/${sp.slug || sp.id}`} className="bg-soft-beige rounded-3xl overflow-hidden group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
                  <div className="aspect-[4/3] bg-beach-sand/30 relative overflow-hidden">
                    {sp.image ? <img src={getImageUrl(sp.image)} alt={sp.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><span className="text-4xl">{categoryIcons[sp.category]}</span></div>}
                    <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-semibold rounded-full text-coconut-green">{categoryIcons[sp.category]} {sp.category}</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-gray-900 group-hover:text-coconut-green transition-colors mb-1">{sp.name}</h3>
                    {sp.location_name && <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3 text-terracotta" />{sp.location_name}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {sp.best_season && <span className="text-xs bg-coconut-green/8 text-coconut-green px-2 py-0.5 rounded-full">{sp.best_season}</span>}
                      {sp.entry_fee && <span className="text-xs bg-sunset-gold/10 text-sunset-gold px-2 py-0.5 rounded-full">{sp.entry_fee}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

const TravelRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 bg-soft-beige rounded-xl flex items-center justify-center flex-shrink-0">{icon}</div>
    <div className="min-w-0"><p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p><p className="text-sm font-medium text-gray-800">{value}</p></div>
  </div>
);

export default PlaceDetailPage;
